import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TEAM_MAX_SEATS, TEAM_MIN_SEATS, TRIAL_DAYS, getPlan, money, periodTotal, yearlySavingPercent } from '../config/plans';
import { IntervalToggle, billingNote, priceLine } from './sales/Pricing';
import { Check } from './sales/shared';
import { planStatus, startCheckout } from '../utils/billing';
import { errorBox, inputClass } from './AuthLayout';
import { usePricing } from '../lib/pricing';

const tones = {
  paid: 'bg-blue-soft text-blue',
  free: 'bg-white border border-line-strong text-ink-muted',
  warn: 'bg-coral-soft text-coral-ink',
};

/** "Free plan" / "Pro" / "Teams · payment due" chip for the dashboard header. */
export function PlanChip({ org }) {
  const s = planStatus(org);
  return <span className={`inline-flex items-center h-7 px-3 rounded-full text-sm font-semibold ${tones[s.tone]}`}>{s.label}</span>;
}

/** Choose monthly/yearly (and seats for Teams), then go to Stripe Checkout for this account. */
export function UpgradePanel({ org, onCancel }) {
  usePricing();
  const status = planStatus(org);
  const plan = getPlan(status.upgradeTo);
  // Stripe gives the trial once per account (create-checkout); none after an earlier subscription
  // or for accounts that already had free access
  const trial = !org.stripe_subscription_id && org.billing_status !== 'complimentary' && TRIAL_DAYS > 0;
  const offer = status.offer === 'team25' && plan.id === 'teams'; // 25% off the first year
  const [interval, setBillingInterval] = useState('year');
  const [seatsText, setSeatsText] = useState(String(TEAM_MIN_SEATS));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const seats = Math.min(TEAM_MAX_SEATS, Math.max(TEAM_MIN_SEATS, Math.floor(Number(seatsText) || TEAM_MIN_SEATS)));
  const price = priceLine(plan, interval);
  const total = periodTotal(plan, interval, seats);

  const go = async () => {
    setBusy(true);
    setError('');
    try {
      const url = await startCheckout({
        org_id: org.id, plan: plan.id, interval,
        ...(plan.perSeat ? { seats } : {}),
        cancel_path: '/',
      });
      window.location.assign(url);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-line p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          {offer && (
            <span className="inline-flex items-center h-6 px-2.5 mb-2 rounded-full bg-coral-soft text-coral-ink text-xs font-bold tracking-wide uppercase">
              Founder offer · 25% off your first year
            </span>
          )}
          <h2 className="text-[22px] font-extrabold tracking-tight">{offer ? 'Add your team' : `Upgrade to ${plan.name}`}</h2>
          <p className="text-[15px] text-ink-muted mt-1">
            {offer ? 'Everyone gets a card with your logo and colors, and you see every lead in one place. Your own card stays included.' : plan.tagline}
          </p>
        </div>
        <IntervalToggle interval={interval} onChange={setBillingInterval} saving={yearlySavingPercent(plan)} />
      </div>

      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[15px]">
        {plan.features.map((f) => {
          const text = typeof f === 'string' ? f : f.text;
          return <li key={text} className="flex gap-2"><Check />{text}</li>;
        })}
      </ul>

      {plan.perSeat && (
        <label className="block max-w-[220px]">
          <span className="block text-sm font-medium text-ink mb-1">People on the team</span>
          <input type="number" min={TEAM_MIN_SEATS} max={TEAM_MAX_SEATS} className={inputClass}
            value={seatsText} onChange={(e) => setSeatsText(e.target.value)} onBlur={() => setSeatsText(String(seats))} disabled={busy} />
          <span className="block text-xs text-ink-muted mt-1">You can change this later in Billing.</span>
        </label>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4 border-t border-line pt-5">
        <div>
          <div>
            {offer && <span className="text-lg text-ink-muted line-through mr-2">{price.amount}</span>}
            <span className="text-[28px] font-extrabold">{offer ? money(plan.price[interval] * 0.75) : price.amount}</span> <span className="text-ink-muted">{price.unit}</span>
          </div>
          <div className="text-sm text-ink-muted">
            {trial ? billingNote(plan, interval) : billingNote(plan, interval).replace(/^\d+-day free trial\. /, '')}
            {plan.perSeat ? ` · ${money(offer ? total * 0.75 : total)} per ${interval === 'year' ? 'year' : 'month'} for ${seats} people` : ''}
            {offer ? ' for the first year, then regular price' : ''}
          </div>
        </div>
        <div className="flex gap-2.5">
          <button type="button" onClick={onCancel} disabled={busy}
            className="h-11 px-4 rounded-xl border border-line-strong bg-white text-ink font-semibold hover:bg-surface disabled:opacity-60">Not now</button>
          <button type="button" onClick={go} disabled={busy}
            className="h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark disabled:opacity-60">
            {busy ? 'Opening checkout...' : trial ? `Start ${TRIAL_DAYS}-day free trial` : 'Continue to payment'}
          </button>
        </div>
      </div>
      {error && <div role="alert" className={errorBox}>{error}</div>}
    </div>
  );
}

/**
 * "This is part of Pro/Teams" with a way to upgrade: owners/admins go to the dashboard's
 * upgrade panel (/?upgrade=<orgId>); everyone else is told to ask their admin.
 */
export function UpgradeNote({ org, canManage, children, className = '' }) {
  const plan = org?.kind === 'company' ? 'Teams' : 'Pro';
  return (
    <div className={`rounded-xl border border-blue/30 bg-blue-soft px-4 py-3 text-[15px] text-ink ${className}`} data-testid="upgrade-note">
      <p>{children}</p>
      {canManage ? (
        <Link to={`/?upgrade=${org.id}`} className="inline-flex items-center h-9 px-4 mt-2.5 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue-dark">
          Upgrade to {plan}
        </Link>
      ) : (
        <p className="text-sm text-ink-muted mt-1">Ask your company admin to upgrade to {plan}.</p>
      )}
    </div>
  );
}
